/**
 * Treatment-plan presentation document (owner research (e)-(h)), imported as
 * `@velora/contracts/txplan-presentation`. Routes under `/v1/txplan`:
 *
 * - `GET   /cases/:caseId/presentation`          versions, the current draft or
 *                                                approved version, gates
 * - `POST  /cases/:caseId/presentation`          start (or refresh) the draft
 * - `PATCH /presentation/:docId`                 edit wording, concern, language,
 *                                                tone, images; an approved
 *                                                version starts a new draft
 * - `POST  /presentation/:docId/generate`        AI wording for the draft (gateway lane
 *                                                `txplan.narrative`)
 * - `POST  /presentation/:docId/approve`         dentist approval (mandatory)
 * - `GET   /presentation/:docId/html?mode=`      review preview, web or present
 * - `GET   /presentation/:docId/pdf`             the approved PDF
 * - `GET   /presentation/:docId/summary?mode=`   one-page chairside summary: review
 *                                                preview, or print (approved only;
 *                                                opens the browser print dialog)
 * - `GET   /presentation/:docId/summary/pdf`     the summary as a PDF (approved only;
 *                                                409 with a print hint when the
 *                                                server has no browser)
 * - `POST  /presentation/:docId/send`            portal / email / text link
 * - `POST  /presentation/:docId/events`          printed, presented, viewed
 *
 * The patient reads the approved version through the existing secure link
 * (`/v1/txplan/p/:token`, after the identity check).
 */
import { z } from 'zod';

export const PRESENTATION_LANGS = ['en', 'es'] as const;
export const PRESENTATION_TONES = ['warm', 'straightforward', 'detailed'] as const;
export const PresentationLangSchema = z.enum(PRESENTATION_LANGS);
export const PresentationToneSchema = z.enum(PRESENTATION_TONES);

export const PresentationSlotSchema = z.object({
  id: z.string(),
  /** `summary` is the one-page chairside summary; the others are pages of the full document. */
  page: z.enum(['summary', 'smile', 'found', 'why', 'options', 'next']),
  label: z.string(),
  text: z.string(),
  source: z.enum(['template', 'ai', 'edited']),
  /** Reading grade (Flesch-Kincaid), null when the text is too short to measure. Staff only. */
  grade: z.number().nullable(),
  flags: z.array(z.string()),
  maxSentences: z.number().int(),
});
export type PresentationSlot = z.infer<typeof PresentationSlotSchema>;

export const GateViewSchema = z.object({
  ok: z.boolean(),
  reason: z.string().nullable(),
  code: z.string(),
});

export const PresentationImageChoiceSchema = z.object({
  findingId: z.string(),
  label: z.string(),
  imageId: z.string().nullable(),
  candidates: z.array(z.object({ imageId: z.string(), label: z.string() })),
});

export const A11yIssueSchema = z.object({
  severity: z.enum(['error', 'warning']),
  code: z.string(),
  message: z.string(),
});

export const PRESENTATION_GOALS = [
  'keep_teeth',
  'chew',
  'smile',
  'comfort',
  'gums',
  'budget',
  'time',
] as const;
export const PresentationGoalSchema = z.enum(PRESENTATION_GOALS);
export const PRESENTATION_PRINCIPLES = [
  'reciprocity',
  'commitment',
  'social_proof',
  'authority',
  'liking',
  'scarcity',
  'unity',
] as const;
export const PresentationPrincipleSchema = z.enum(PRESENTATION_PRINCIPLES);

/** One Cialdini rule as applied to this plan (data source and guardrail in CIALDINI.md). */
export const PrincipleOutcomeSchema = z.object({
  id: PresentationPrincipleSchema,
  name: z.string(),
  enabled: z.boolean(),
  applied: z.boolean(),
  detail: z.string(),
  source: z.string(),
  guardrail: z.string(),
});
export type PrincipleOutcomeView = z.infer<typeof PrincipleOutcomeSchema>;

/** Automatic AI wording for the draft. */
export const PresentationAiSchema = z.object({
  status: z.enum(['none', 'queued', 'running', 'done', 'failed']),
  /** The wording was made from the current plan and facts (cache hit). */
  fresh: z.boolean(),
  model: z.string().nullable(),
  /** AI-written slots / all slots. */
  aiSlots: z.number().int(),
  totalSlots: z.number().int(),
  /** Why the AI did not write (some of) the wording, in plain words. */
  reason: z.string().nullable(),
});

export const PresentationDocSchema = z.object({
  id: z.uuid(),
  version: z.number().int(),
  status: z.enum(['draft', 'approved', 'superseded']),
  lang: PresentationLangSchema,
  tone: PresentationToneSchema,
  concern: z.string().nullable(),
  includeAppendix: z.boolean(),
  slots: z.array(PresentationSlotSchema),
  images: z.array(PresentationImageChoiceSchema),
  aiModel: z.string().nullable(),
  aiNote: z.string().nullable(),
  approvedByName: z.string().nullable(),
  approvedAt: z.string().nullable(),
  htmlSha256: z.string().nullable(),
  pdfSha256: z.string().nullable(),
  pdfError: z.string().nullable(),
  a11y: z.array(A11yIssueSchema),
  /** The plan changed after this version was made. */
  planChanged: z.boolean(),
  gates: z.object({ present: GateViewSchema, print: GateViewSchema, send: GateViewSchema }),
  createdAt: z.string(),
  /** What matters to the patient (TC quick-capture). */
  goals: z.array(PresentationGoalSchema).default([]),
  principles: z.array(PrincipleOutcomeSchema).default([]),
  doctorNote: z.string().nullable().default(null),
  planOptions: z
    .object({ suggestSequence: z.boolean(), alternatives: z.boolean() })
    .default({ suggestSequence: true, alternatives: true }),
  ai: PresentationAiSchema.optional(),
  /** Facts for staff only (suggested sequence, unknown coverage, financing set-up). */
  staffNotes: z.array(z.string()).default([]),
  /** Some insurance coverage on the recommended option is not verified (a verify-benefits task helps). */
  coverageUnverified: z.boolean().default(false),
});
export type PresentationDoc = z.infer<typeof PresentationDocSchema>;

export const PresentationEventSchema = z.object({
  kind: z.string(),
  channel: z.string().nullable(),
  version: z.number().int(),
  actorName: z.string(),
  at: z.string(),
});

export const PresentationStateSchema = z.object({
  caseId: z.uuid(),
  current: PresentationDocSchema.nullable(),
  versions: z.array(
    z.object({
      id: z.uuid(),
      version: z.number().int(),
      status: z.enum(['draft', 'approved', 'superseded']),
      lang: PresentationLangSchema,
      approvedByName: z.string().nullable(),
      approvedAt: z.string().nullable(),
    }),
  ),
  events: z.array(PresentationEventSchema),
  canEdit: z.boolean(),
  canApprove: z.boolean(),
  /** An eligible model (on-prem or BAA vendor) is configured for `txplan.narrative`. */
  aiAvailable: z.boolean(),
  /** Texts and emails: the master automations gate is installed on this server. */
  outboundAvailable: z.boolean(),
  defaultLang: PresentationLangSchema,
  defaultTone: PresentationToneSchema,
  /** TC role test fix 3: who approves for this plan ("Dr. Lindenridge"). */
  approverName: z.string().nullable().optional(),
  /** An open "Request approval" on the current draft. */
  approvalRequest: z
    .object({
      requestedFromName: z.string(),
      requestedByName: z.string(),
      requestedAt: z.string(),
    })
    .nullable()
    .optional(),
});
export type PresentationState = z.infer<typeof PresentationStateSchema>;

export const CreatePresentationSchema = z.object({
  lang: PresentationLangSchema.optional(),
  tone: PresentationToneSchema.optional(),
  /** AI wording for the new draft (default on since 2026-10-21; template is the fallback). */
  useAi: z.boolean().default(true),
  /**
   * Opening the presentation: make the draft when there is no version yet,
   * and refresh its AI wording when it is missing or stale. Never supersedes
   * an approved version.
   */
  auto: z.boolean().optional(),
});
export type CreatePresentation = z.infer<typeof CreatePresentationSchema>;

export const UpdatePresentationSchema = z
  .object({
    slots: z.record(z.string(), z.string().max(700)).optional(),
    /** Slot ids to put back to the template wording. */
    resetSlots: z.array(z.string()).optional(),
    concern: z.string().max(600).nullable().optional(),
    lang: PresentationLangSchema.optional(),
    tone: PresentationToneSchema.optional(),
    includeAppendix: z.boolean().optional(),
    /** Finding id to image id, or null for no image (placeholder). */
    images: z.record(z.string(), z.string().nullable()).optional(),
    goals: z.array(PresentationGoalSchema).max(7).optional(),
    /** Turn a Cialdini rule off (false) or back on (true) for this plan. */
    principles: z.partialRecord(PresentationPrincipleSchema, z.boolean()).optional(),
    /** The dentist's own short note (authority); never generated. */
    doctorNote: z.string().max(400).nullable().optional(),
    planOptions: z
      .object({ suggestSequence: z.boolean().optional(), alternatives: z.boolean().optional() })
      .strict()
      .optional(),
  })
  .strict();
export type UpdatePresentation = z.infer<typeof UpdatePresentationSchema>;

export const VerifyBenefitsResultSchema = z.object({
  created: z.boolean(),
  title: z.string(),
});

export const PresentationSendSchema = z.object({
  channel: z.enum(['portal', 'email', 'sms']),
});
export type PresentationSend = z.infer<typeof PresentationSendSchema>;

export const PresentationSendResultSchema = z.object({
  channel: z.enum(['portal', 'email', 'sms']),
  status: z.enum(['sent', 'queued', 'available', 'held', 'blocked', 'unavailable', 'failed']),
  reason: z.string().nullable(),
  state: PresentationStateSchema,
});
export type PresentationSendResult = z.infer<typeof PresentationSendResultSchema>;

export const PresentationTrackSchema = z.object({
  kind: z.enum(['printed', 'presented', 'viewed']),
  page: z.string().max(40).optional(),
  ms: z.number().int().min(0).max(3_600_000).optional(),
});
